import { apiEmployees } from "@/api/employees/employee.api";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Input,
  LinkTo,
  ListItem,
  Stack,
  Text,
  Title,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { z } from "zod";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { handleErrors } = useErrorHandle();
  useTitle(t("core:restore_password"));

  const ForgotPasswordSchemaObject = z.object({
    email: z
      .string({ error: t("errors:field_required") })
      .min(1, t("errors:field_required"))
      .regex(z.regexes.html5Email, t("errors:field_email_required")),
  });

  type ForgotPasswordSchema = z.infer<typeof ForgotPasswordSchemaObject>;

  const { showInfo } = useToasts();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ForgotPasswordSchema>({
    mode: "onSubmit",
    resolver: zodResolver(ForgotPasswordSchemaObject),
  });

  const onSubmit = handleSubmit(async (loginData) => {
    setLoading(true);
    const { data, error } = await apiEmployees.forgotPassword(loginData.email);
    if (data) {
      showInfo({ title: t("core:email_with_password_reset_was_sent") });
    } else {
      handleErrors(error, control);
    }
    setLoading(false);
  });

  const [isLoading, setLoading] = useState<boolean>(false);

  return (
    <FullScreenCard>
      <Stack gap={"sm"} className={"w-full sm:max-w-md "}>
        <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
          <Stack gap={"2xl"}>
            <a
              href={import.meta.env.VITE_LANDING_URL + "/"}
              className="flex items-center mb-6 text-2xl font-semibold gap-x-[6px]"
            >
              <img src="/logo.png" alt="Skailer" className="h-[28px] w-auto" />
              <Text className={"text-[22px] leading-[24px]"}>{t("core:app_name")}</Text>
            </a>
            <Stack gap={"lg"}>
              <Title header={t("core:restore_password")} size={"lg"} />
              <form className="" onSubmit={onSubmit}>
                <Input
                  label={t("core:email")}
                  type="email"
                  {...register("email")}
                  error={errors.email?.message}
                />
              </form>
              <ServerErrorField errors={errors} />
              <Button
                type={"submit"}
                size="lg"
                onClick={onSubmit}
                className="w-full"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {t("common:restore")}
              </Button>
            </Stack>
          </Stack>
        </Box>
        <Stack className={"px-6 lg:px-8"}>
          <ListItem
            title={t("core:already_have_an_account")}
            valueSlot={<LinkTo to={"/login"}>{t("common:login")}</LinkTo>}
          />
        </Stack>
      </Stack>
    </FullScreenCard>
  );
}
